<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { donationsRouteForPeriod } from '@/helpers/givingRecord'
import ClaimGuideStepIndex from '@/components/dashboard/claim/ClaimGuideStepIndex.vue'
import ClaimableTotalHint from '@/components/dashboard/claim/ClaimableTotalHint.vue'

/**
 * Step 4 of the paid guide, and the collapsed year summary once claimed.
 * Marking claimed is a local record only: we do not contact HMRC. Undo lives
 * in the expanded details. The year summary is a short status plus links to
 * open the steps again or view the donations in the claim.
 */
const props = withDefaults(
  defineProps<{
    variant?: 'step' | 'year'
    totalGivenPence?: number
    donationCount?: number
    claimablePence?: number | null
  }>(),
  {
    variant: 'step',
    totalGivenPence: 0,
    donationCount: 0,
    claimablePence: null,
  }
)

const emit = defineEmits<{
  expand: []
}>()

const { t } = useI18n()
const { profile, setClaimed } = useClaimTaxBack()

const yearLabel = claimTaxYearConfig.yearLabel
const donationsLink = donationsRouteForPeriod(claimTaxYearConfig.periodKey)
const isSaving = ref(false)
const saveError = ref<string | null>(null)
const heading = ref<HTMLElement | null>(null)
const isYearSummary = computed(() => props.variant === 'year')
const givenDisplay = computed(() =>
  formatMoneyFromPence(props.totalGivenPence)
)
const claimableDisplay = computed(() =>
  props.claimablePence != null
    ? formatMoneyFromPence(props.claimablePence)
    : t('Dashboard.ClaimTaxBack.Guide.Metrics.ClaimableNotYet')
)

const save = async (claimed: boolean) => {
  saveError.value = null
  isSaving.value = true
  try {
    await setClaimed(claimed)
  } catch (error) {
    console.error('Failed to update claim marked state', error)
    saveError.value = t('Dashboard.ClaimTaxBack.Guide.Claimed.ErrorSave')
  } finally {
    isSaving.value = false
  }
}

onMounted(async () => {
  if (!isYearSummary.value) return
  await nextTick()
  heading.value?.focus()
})
</script>

<template>
  <section
    class="sa-claim-guide__step"
    :class="{ 'sa-claim-guide__step--collapsed': isYearSummary }"
    :aria-labelledby="
      profile?.claimed
        ? 'claim-guide-claimed-done-heading'
        : 'claim-guide-claimed-heading'
    "
  >
    <div class="sa-claim-guide__step-header">
      <ClaimGuideStepIndex
        :number="isYearSummary ? undefined : 4"
        :done="Boolean(profile?.claimed)"
      />
      <h3
        :id="
          profile?.claimed
            ? 'claim-guide-claimed-done-heading'
            : 'claim-guide-claimed-heading'
        "
        ref="heading"
        class="sa-claim-guide__step-title"
        :tabindex="isYearSummary ? -1 : undefined"
      >
        {{
          profile?.claimed
            ? t('Dashboard.ClaimTaxBack.Guide.Claimed.DoneTitle')
            : t('Dashboard.ClaimTaxBack.Guide.Claimed.Title')
        }}
      </h3>
    </div>

    <div v-if="isYearSummary" class="sa-claim-guide__summary">
      <dl class="sa-claim-guide__facts">
        <div class="sa-claim-guide__fact">
          <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Metrics.Given') }}</dt>
          <dd>{{ givenDisplay }}</dd>
        </div>
        <div class="sa-claim-guide__fact">
          <dt>{{ t('Dashboard.ClaimTaxBack.Guide.Metrics.Claimable') }}</dt>
          <dd class="sa-claim-guide__fact-claimable">
            <!-- Hint sits next to the total: HMRC decides the final relief. -->
            <ClaimableTotalHint :show-hint="props.claimablePence != null">
              {{ claimableDisplay }}
            </ClaimableTotalHint>
          </dd>
        </div>
      </dl>
      <div class="sa-claim-guide__actions">
        <button
          type="button"
          class="sa-btn-secondary sa-btn-secondary--compact"
          :aria-expanded="false"
          aria-controls="claim-guide-year-body"
          @click="emit('expand')"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Claimed.Expand') }}
        </button>
        <router-link
          class="sa-btn-secondary sa-btn-secondary--compact"
          :to="donationsLink"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Claimed.ViewDonations') }}
        </router-link>
      </div>
    </div>

    <div v-else class="sa-claim-guide__step-main">
      <p class="sa-claim-guide__step-body">
        {{
          profile?.claimed
            ? t('Dashboard.ClaimTaxBack.Guide.Claimed.DoneBody', {
                year: yearLabel,
              })
            : t('Dashboard.ClaimTaxBack.Guide.Claimed.Body')
        }}
      </p>
      <div class="sa-claim-guide__actions">
        <button
          v-if="!profile?.claimed"
          type="button"
          class="sa-btn-primary"
          :disabled="isSaving"
          :aria-busy="isSaving"
          @click="save(true)"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Claimed.Mark') }}
        </button>
        <button
          v-else
          type="button"
          class="sa-claim-link-btn"
          :disabled="isSaving"
          :aria-busy="isSaving"
          @click="save(false)"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Claimed.Undo') }}
        </button>
      </div>
      <p v-if="saveError" class="sa-field-error" role="alert">
        {{ saveError }}
      </p>
    </div>
  </section>
</template>
